import React, { useState } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { FaMars, FaVenus, FaGenderless } from 'react-icons/fa';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import maleOption from '../../assets/images/male-option.png';
import femaleOption from '../../assets/images/female-option.png';
import nonBinaryOption from '../../assets/images/non-binary-option.png';
import AdvancedFilters from '../AdvancedFilters/AdvancedFilters';
import './BasicFilters.css';

const BasicFilters = ({ onApply, onClear, initialFilters = {} }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [basicFilters, setBasicFilters] = useState({
    gender: initialFilters.gender || '',
    ageMin: initialFilters.age_min || 18,
    ageMax: initialFilters.age_max || 35,
    distance: initialFilters.distance || 50,
  });

  const handleGenderSelect = (gender) => {
    setBasicFilters(prev => ({ ...prev, gender }));
  };

  const handleAgeRangeChange = (values) => {
    setBasicFilters(prev => ({
      ...prev,
      ageMin: values[0],
      ageMax: values[1]
    }));
  };

  const handleDistanceChange = (value) => {
    setBasicFilters(prev => ({ ...prev, distance: value }));
  };

  const handleBasicApply = () => {
    const filterParams = {
      gender: basicFilters.gender,
      age_min: basicFilters.ageMin,
      age_max: basicFilters.ageMax,
      distance: basicFilters.distance
    };
    onApply(filterParams);
  };

  const handleBasicClear = () => {
    const defaultFilters = {
      gender: '',
      ageMin: 18,
      ageMax: 35,
      distance: 50
    };
    setBasicFilters(defaultFilters);
    onClear(defaultFilters);
  };

  const handleAdvancedApply = (advancedFilters) => {
    const combinedFilters = {
      ...basicFilters,
      ...advancedFilters
    };
    onApply(combinedFilters);
  };

  const handleAdvancedClear = () => {
    const defaultFilters = {
      gender: '',
      ageMin: 18,
      ageMax: 35,
      distance: 50
    };
    setBasicFilters(defaultFilters);
    onClear(defaultFilters);
  };

  return (
    <div className="">
      <div className="filter-header text-center">
        <h4>Basic Filter</h4>
      </div>

      {/* Filter Type Selector */}
      <div className="filter-type-selector">
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic
          </button>
          <button 
            className={`filter-tab ${activeTab === 'advanced' ? 'active' : ''}`}
            onClick={() => setActiveTab('advanced')}
          >
            Advance
          </button>
        </div>
      </div>

      {activeTab === 'basic' ? (
        <>
          {/* Gender Filter */}
          <div className="filter-section">
            <h5 className="filter-label">Gender</h5>
            <div className="gender-options">
              <div
                className={`gender-option male-option ${basicFilters.gender === 'man' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('man')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaMars size={20} />
                  </div>
                  <img src={maleOption} alt="Male" className="img-fluid" />
                </div>
                <span className="gender-title">Male</span>
              </div>
              <div
                className={`gender-option female-option ${basicFilters.gender === 'woman' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('woman')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaVenus size={20} />
                  </div>
                  <img src={femaleOption} alt="Female" className="img-fluid" />
                </div>
                <span className="gender-title">Female</span>
              </div>
              <div
                className={`gender-option non-binary-option ${basicFilters.gender === 'non-binary' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('non-binary')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaGenderless size={20} />
                  </div>
                  <img src={nonBinaryOption} alt="Non Binary" className="img-fluid" />
                </div>
                <span className="gender-title">Non Binary</span>
              </div>
            </div>
          </div>

          {/* Distance Filter */}
          <div className="filter-section">
            <h5 className="filter-label">Distance</h5>
            <div className="distance-slider">
              <div className="distance-display">
                <span>{basicFilters.distance} Miles</span>
              </div>
              <Slider
                min={0}
                max={250}
                value={basicFilters.distance}
                onChange={handleDistanceChange}
                styles={{
                  rail: {
                    backgroundColor: '#E0E0E0',
                    height: 8
                  },
                  track: {
                    backgroundColor: '#5494DA',
                    height: 8
                  },
                  handle: {
                    borderColor: '#5494DA',
                    backgroundColor: '#5494DA',
                    opacity: 1,
                    boxShadow: 'none',
                    width: 20,
                    height: 20,
                    marginTop: -6,
                    border: 'none'
                  }
                }}
              />
            </div>
          </div>

          {/* Age Filter */}
          <div className="filter-section">
            <h5 className="filter-label">Age Between 18 to 90</h5>
            <div className="age-slider">
              <div className="age-range-display">
                {basicFilters.ageMin} - {basicFilters.ageMax}
              </div>
              <Slider
                range
                min={18}
                max={90}
                value={[basicFilters.ageMin, basicFilters.ageMax]}
                onChange={handleAgeRangeChange}
                styles={{
                  rail: {
                    backgroundColor: '#E0E0E0',
                    height: 8
                  },
                  track: {
                    backgroundColor: '#5494DA',
                    height: 8
                  },
                  handle: {
                    borderColor: '#5494DA',
                    backgroundColor: '#5494DA',
                    opacity: 1,
                    boxShadow: 'none',
                    width: 20,
                    height: 20,
                    marginTop: -6,
                    border: 'none'
                  }
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="filter-actions">
            <Button 
              variant="outline-primary" 
              className="clear-btn"
              onClick={handleBasicClear}
            >
              Clear
            </Button>
            <Button 
              variant="primary" 
              className="apply-btn"
              onClick={handleBasicApply}
            >
              Apply
            </Button>
          </div>
        </>
      ) : (
        <AdvancedFilters 
          onApply={handleAdvancedApply}
          onClear={handleAdvancedClear}
          initialFilters={initialFilters}
        />
      )}
    </div>
  );
};

export default BasicFilters;
